import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSettlements, createSettlement, deleteSettlement, getMonthlySettlementReport } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Bank, Plus, Trash, CalendarBlank, CurrencyInr, ArrowsDownUp,
  DownloadSimple, ChartBar
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);
const SERVICE_TYPES = ['AEPS', 'RTGS', 'NEFT', 'IMPS', 'UPI', 'Cash', 'Other'];

export default function SettlementsPage() {
  const { user } = useAuth();
  const [settlements, setSettlements] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [providerFilter, setProviderFilter] = useState('');

  // Form
  const initForm = {
    settlement_date: new Date().toISOString().split('T')[0],
    amount: '', service_type: 'AEPS', transaction_type: 'Settlement',
    reference_number: '', provider: 'GoPayment', bank_account: '',
    charges: '', gst_amount: '', tds: '', commission: '', note: '',
  };
  const [form, setForm] = useState(initForm);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (providerFilter) params.provider = providerFilter;
      const res = await getSettlements(params);
      setSettlements(res.data.settlements || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      toast.error('Load failed');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, providerFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Amount daalo'); return; }
    setSubmitting(true);
    try {
      const res = await createSettlement({
        ...form,
        amount: parseFloat(form.amount),
        charges: parseFloat(form.charges || 0),
        gst_amount: parseFloat(form.gst_amount || 0),
        tds: parseFloat(form.tds || 0),
        commission: parseFloat(form.commission || 0),
      });
      toast.success(res.data.message);
      setForm(initForm);
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this settlement?')) return;
    try {
      await deleteSettlement(id);
      toast.success('Deleted');
      fetchData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const netAmount = (parseFloat(form.amount || 0) - parseFloat(form.charges || 0)).toFixed(2);

  if (!isAdmin) return <div className="p-4 text-red-600">Admin access required.</div>;

  return (
    <div className="space-y-5" data-testid="settlements-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="settlements-title">Cash Settlements</h1>
          <p className="text-sm text-gray-500 mt-0.5">AEPS/RTGS/NEFT settlements track karo — GoPayment, bank transfers</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#002FA7] hover:bg-[#002590] gap-2 self-start" data-testid="new-settlement-btn">
          <Plus size={18} /> New Settlement
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="settlement-stats">
        <div className="bg-white border rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1"><ArrowsDownUp size={14} /> Total</div>
          <p className="text-lg font-bold text-gray-900">{summary.total_settlements || 0}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <div className="text-xs text-green-600 mb-1 flex items-center gap-1"><CurrencyInr size={14} /> Amount</div>
          <p className="text-lg font-bold text-green-700">{fmt(summary.total_amount)}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <div className="text-xs text-orange-600 mb-1 flex items-center gap-1"><Bank size={14} /> Charges</div>
          <p className="text-lg font-bold text-orange-700">{fmt(summary.total_charges)}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <div className="text-xs text-blue-600 mb-1 flex items-center gap-1"><ChartBar size={14} /> Net</div>
          <p className="text-lg font-bold text-blue-700">{fmt(summary.total_net)}</p>
        </div>
      </div>

      {/* New Settlement Form */}
      {showForm && (
        <div className="bg-white border-2 border-[#002FA7]/20 rounded-lg p-4 space-y-3" data-testid="settlement-form">
          <h2 className="text-base font-semibold text-gray-900">New Settlement Entry</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.settlement_date} onChange={e => setField('settlement_date', e.target.value)} data-testid="settle-date" />
            </div>
            <div>
              <Label className="text-xs">Amount *</Label>
              <Input type="number" value={form.amount} onChange={e => setField('amount', e.target.value)} placeholder="50000" data-testid="settle-amount" />
            </div>
            <div>
              <Label className="text-xs">Service Type</Label>
              <select value={form.service_type} onChange={e => setField('service_type', e.target.value)} className="w-full border rounded-md h-10 px-3 text-sm" data-testid="settle-service">
                {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Provider</Label>
              <Input value={form.provider} onChange={e => setField('provider', e.target.value)} placeholder="GoPayment" data-testid="settle-provider" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Reference No.</Label>
              <Input value={form.reference_number} onChange={e => setField('reference_number', e.target.value)} placeholder="TXN123456" data-testid="settle-ref" />
            </div>
            <div>
              <Label className="text-xs">Charges</Label>
              <Input type="number" value={form.charges} onChange={e => setField('charges', e.target.value)} placeholder="0" data-testid="settle-charges" />
            </div>
            <div>
              <Label className="text-xs">GST Amount</Label>
              <Input type="number" value={form.gst_amount} onChange={e => setField('gst_amount', e.target.value)} placeholder="0" data-testid="settle-gst" />
            </div>
            <div>
              <Label className="text-xs">TDS</Label>
              <Input type="number" value={form.tds} onChange={e => setField('tds', e.target.value)} placeholder="0" data-testid="settle-tds" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Bank Account</Label>
              <Input value={form.bank_account} onChange={e => setField('bank_account', e.target.value)} placeholder="SBI / HDFC" data-testid="settle-bank" />
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Input value={form.note} onChange={e => setField('note', e.target.value)} placeholder="Remark..." data-testid="settle-note" />
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-3 w-full">
                <div className="text-sm">Net: <strong className="text-green-700">{fmt(netAmount)}</strong></div>
                <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 gap-1 flex-1" data-testid="submit-settlement-btn">
                  <Plus size={14} /> {submitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-white border rounded-lg p-3" data-testid="settlement-filters">
        <CalendarBlank size={14} className="text-gray-400" />
        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-xs w-[120px]" data-testid="settle-filter-start" />
        <span className="text-gray-400 text-xs">to</span>
        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 text-xs w-[120px]" data-testid="settle-filter-end" />
        <Input value={providerFilter} onChange={e => setProviderFilter(e.target.value)} className="h-8 text-xs w-[130px]" placeholder="Provider..." data-testid="settle-filter-provider" />
        {(startDate || endDate || providerFilter) && (
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => { setStartDate(''); setEndDate(''); setProviderFilter(''); }}>Clear</Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden" data-testid="settlement-table">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : settlements.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Bank size={40} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Koi settlement nahi mila</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Charges</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settlements.map(s => (
                  <TableRow key={s.id} data-testid={`settle-row-${s.id}`}>
                    <TableCell className="text-sm whitespace-nowrap">{s.settlement_date}</TableCell>
                    <TableCell><span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{s.provider}</span></TableCell>
                    <TableCell><span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{s.service_type}</span></TableCell>
                    <TableCell className="text-xs font-mono text-gray-600">{s.reference_number || '-'}</TableCell>
                    <TableCell className="text-right text-sm font-medium text-green-700">{fmt(s.amount)}</TableCell>
                    <TableCell className="text-right text-sm text-orange-600">{s.charges > 0 ? fmt(s.charges) : '-'}</TableCell>
                    <TableCell className="text-right text-sm text-gray-500">{s.gst_amount > 0 ? fmt(s.gst_amount) : '-'}</TableCell>
                    <TableCell className="text-right text-sm font-semibold text-blue-700">{fmt(s.net_amount)}</TableCell>
                    <TableCell className="text-xs text-gray-500 max-w-[150px] truncate">{s.note || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                        onClick={() => handleDelete(s.id)} data-testid={`delete-settle-${s.id}`}>
                        <Trash size={15} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
